import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchNetworkUserDetails } from '../../../store/slices/usersSlice';
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, Award, Package, Users, ShoppingBag } from 'lucide-react';
import Loader from '../../common/Loader';
import NetworkTreeVisualization from './NetworkTreeVisualization';

const NetworkUserDetailsPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { networkUserDetails } = useAppSelector((state) => state.users);
    const { data, loading, error } = networkUserDetails;

    useEffect(() => {
        if (mobile) {
            dispatch(fetchNetworkUserDetails(mobile));
        }
    }, [dispatch, mobile]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-500 mb-4">Error: {error}</div>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded">Go Back</button>
            </div>
        );
    }

    if (!data || !data.user) {
        return null;
    }

    const { user, stats } = data;


    // Milestones from backend - Only show achieved or next target
    const milestones = stats?.milestones || [];
    const displayMilestones = [...milestones]
        .filter(m => m.is_achieved || m.is_next_target)
        .sort((a, b) => a.threshold - b.threshold);

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            <div className="mb-6 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                    <ArrowLeft size={24} />
                </button>
                <h1 className="text-2xl font-bold text-gray-800 tracking-tight">User Network Details</h1>
            </div>

            <div className="max-w-7xl mx-auto space-y-8">
                {/* User Profile Card */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                    <div className="flex flex-col md:flex-row gap-8">
                        <div className="flex-shrink-0">
                            <div className="w-28 h-28 bg-slate-50 rounded-full flex items-center justify-center overflow-hidden border-4 border-white shadow-xl">
                                <User size={56} className="text-slate-300" />
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="flex flex-wrap gap-3 items-center mb-3">
                                <h2 className="text-3xl font-black text-slate-900 leading-none">{user.first_name} {user.last_name}</h2>
                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${user.verified ? 'bg-emerald-100 text-emerald-600 border border-emerald-200' : 'bg-rose-100 text-rose-600 border border-rose-200'}`}>
                                    {user.verified ? 'Verified' : 'Pending'}
                                </span>
                                <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-indigo-100 text-indigo-600 border border-indigo-200">
                                    {user.role}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-12 text-sm text-slate-500 font-bold mt-5">
                                <div className="flex items-center gap-3"><Phone size={18} className="text-slate-400" /> {user.mobile}</div>
                                <div className="flex items-center gap-3"><Mail size={18} className="text-slate-400" /> {user.email || 'N/A'}</div>
                                <div className="flex items-center gap-3"><MapPin size={18} className="text-slate-400" /> {user.city}, {user.state} {user.pincode}</div>
                                <div className="flex items-center gap-3"><Calendar size={18} className="text-slate-400" /> Joined {new Date(user.user_created_date).toLocaleDateString()}</div>
                            </div>

                            <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-slate-50">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-indigo-50 rounded-2xl text-indigo-600 shadow-sm"><Users size={22} /></div>
                                    <div>
                                        <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.15em] mb-1">Network Size</div>
                                        <div className="text-lg font-black text-slate-800 leading-none">{stats?.total_network_size || 0}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600 shadow-sm"><ShoppingBag size={22} /></div>
                                    <div>
                                        <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.15em] mb-1">Paid Orders</div>
                                        <div className="text-lg font-black text-slate-800 leading-none">{stats?.paid_orders_count || 0}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-amber-50 rounded-2xl text-amber-600 shadow-sm"><Package size={22} /></div>
                                    <div>
                                        <div className="text-[10px] text-slate-400 font-black uppercase tracking-[0.15em] mb-1">Paid Units</div>
                                        <div className="text-lg font-black text-slate-800 leading-none">{stats?.paid_units_count || 0}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-10">
                    {/* Coins Overview */}
                    <div className="space-y-5">
                        <div className="flex items-center gap-3 px-2">
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600"><Award size={18} /></div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Coins Overview</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Total Coins</div>
                                <div className="text-2xl font-black text-amber-600 flex items-center gap-2 leading-none"><Award className="w-6 h-6" />{(stats?.total_coins || 0).toLocaleString('en-IN')}</div>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Spent Coins</div>
                                <div className="text-2xl font-black text-rose-500 leading-none">{(stats?.spending_coins || 0).toLocaleString('en-IN')}</div>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Balance</div>
                                <div className="text-2xl font-black text-emerald-500 leading-none">{(stats?.remaining_coins || 0).toLocaleString('en-IN')}</div>
                            </div>
                        </div>
                    </div>

                    {/* Direct Referrals Section */}
                    <div className="space-y-8">
                        <div className="flex items-center gap-3 px-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><Users size={18} /></div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Direct Referrals</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Referrals</div>
                                <div className="text-2xl font-black text-slate-800 leading-none">{stats?.direct_referrals_count || 0}</div>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Total Units</div>
                                <div className="text-2xl font-black text-blue-600 leading-none">{stats?.direct_referrals_units || 0}</div>
                            </div>
                            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] hover:shadow-lg">
                                <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-3">Direct Coins</div>
                                <div className="text-2xl font-black text-amber-600 flex items-center gap-2 leading-none"><Award className="w-6 h-6" />{(stats?.direct_referrals_coins || 0).toLocaleString('en-IN')}</div>
                            </div>
                        </div>

                        {/* Direct Rewards Status with Professional Tracking Bar */}
                        {displayMilestones.length > 0 && (
                            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 p-6 relative">
                                <div className="mb-8 flex items-center justify-between">
                                    <h4 className="text-xl font-black text-slate-800 tracking-tight">Reward Milestones Tracking</h4>
                                    <div className="flex items-center gap-2">
                                        <div className="group relative">
                                            <div className="bg-slate-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm cursor-help transition-all hover:bg-slate-700">
                                                Current: {stats?.direct_referrals_units_offer_activated || 0} Units
                                            </div>
                                            {/* Tooltip */}
                                            <div className="absolute bottom-full right-0 mb-2 w-48 scale-0 transition-all rounded bg-gray-900 p-2 text-[10px] text-white group-hover:scale-100 z-[100] shadow-xl border border-gray-700 font-medium leading-relaxed">
                                                <div className="relative">
                                                    This count is calculated based on units achieved while the offer is active.
                                                    <div className="absolute top-full right-4 -mt-1 h-2 w-2 rotate-45 bg-gray-900 border-r border-b border-gray-700"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative overflow-x-auto no-scrollbar pb-10 px-8">
                                    {/* Progress Calculation Logic */}
                                    {/* Units Pointer Overlay - Absolute positioning over the whole bar */}
                                    {(() => {
                                        const currentUnits = stats?.direct_referrals_units_offer_activated || 0;
                                        const milestoneThresholds = displayMilestones.map((m: any) => m.threshold);
                                        const allThresholds = [0, ...milestoneThresholds];

                                        let progressPercentage = 0;
                                        if (allThresholds.length > 1) {
                                            const lastT = allThresholds[allThresholds.length - 1];
                                            if (currentUnits >= lastT) {
                                                progressPercentage = 100;
                                            } else if (currentUnits > 0) {
                                                for (let i = 0; i < allThresholds.length - 1; i++) {
                                                    if (currentUnits >= allThresholds[i] && currentUnits <= allThresholds[i + 1]) {
                                                        const segmentWidth = 100 / (allThresholds.length - 1);
                                                        const segmentProgress = (currentUnits - allThresholds[i]) / (allThresholds[i + 1] - allThresholds[i]);
                                                        progressPercentage = (i * segmentWidth) + (segmentProgress * segmentWidth);
                                                        break;
                                                    }
                                                }
                                            }
                                        }

                                        return (
                                            <div className="absolute top-[46px] left-[90px] right-[90px] h-0.5 z-20 pointer-events-none">
                                                <div
                                                    className="h-full relative transition-all duration-1000 ease-out"
                                                    style={{ width: `${progressPercentage}%` }}
                                                >
                                                    {/* Pointer removed from here */}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    <div className="flex flex-row min-w-max relative z-10 px-4">
                                        {(() => {
                                            const currentUnits = stats?.direct_referrals_units_offer_activated || 0;
                                            const milestoneThresholds = displayMilestones.map((m: any) => m.threshold);
                                            const allThresholds = [0, ...milestoneThresholds];

                                            return (
                                                <>
                                                    {/* Start Marker (0 Units) */}
                                                    <div className="flex flex-col items-center w-[180px] relative">
                                                        <div className="text-center mb-1 flex flex-col justify-end h-8">
                                                            <span className="text-[11px] font-bold text-slate-600">0 Units</span>
                                                        </div>
                                                        <div className="relative w-full flex justify-center mb-2">
                                                            {/* Line Segment to Next Milestone */}
                                                            {allThresholds.length > 1 && (
                                                                <div className="absolute top-1/2 -translate-y-1/2 left-[calc(50%+10px)] right-[calc(-50%+10px)] h-0.5 bg-slate-200 z-0">
                                                                    <div
                                                                        className="h-full bg-emerald-500 transition-all duration-1000"
                                                                        style={{
                                                                            width: `${Math.min(100, Math.max(0, (currentUnits - allThresholds[0]) / (allThresholds[1] - allThresholds[0]) * 100))}%`
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}

                                                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-emerald-500 text-white text-[9px] font-bold z-10 border-2 border-white shadow-sm">
                                                                ✓
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-center text-center px-2 w-full">
                                                            <div className="text-xs font-bold text-emerald-600 mb-1">Start</div>
                                                        </div>
                                                    </div>

                                                    {/* Milestone Nodes */}
                                                    {displayMilestones.map((m: any, idx: number) => {
                                                        const label = m.reward;
                                                        const achieved = m.is_achieved === true;
                                                        const isNext = m.is_next_target === true;
                                                        const isLast = idx === displayMilestones.length - 1;

                                                        // indices: allThresholds[0]=0, allThresholds[1]=threshold1...
                                                        // so current segment is from allThresholds[idx+1] to allThresholds[idx+2]
                                                        const startT = allThresholds[idx + 1];
                                                        const endT = allThresholds[idx + 2];

                                                        return (
                                                            <div key={idx} className="flex flex-col items-center w-[180px] relative">
                                                                <div className="text-center mb-1 flex flex-col justify-end h-8">
                                                                    <span className={`text-[11px] font-bold ${achieved ? 'text-slate-600' : 'text-slate-400'}`}>
                                                                        {m.threshold} Units
                                                                    </span>
                                                                </div>

                                                                <div className="relative w-full flex justify-center mb-2">
                                                                    {/* Line Segment to Next Milestone */}
                                                                    {!isLast && (
                                                                        <div className="absolute top-1/2 -translate-y-1/2 left-[calc(50%+10px)] right-[calc(-50%+10px)] h-0.5 bg-slate-200 z-0">
                                                                            <div
                                                                                className="h-full bg-emerald-500 transition-all duration-1000"
                                                                                style={{
                                                                                    width: `${Math.min(100, Math.max(0, (currentUnits - startT) / (endT - startT) * 100))}%`
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-2 z-10 transition-all duration-500 ${achieved ? 'bg-emerald-500 border-white text-white shadow-sm' : 'bg-white border-slate-200 text-slate-400'}`}>
                                                                        {achieved ? '✓' : idx + 1}
                                                                    </div>
                                                                </div>

                                                                <div className="flex flex-col items-center text-center px-4 w-full">
                                                                    <div className={`text-xs font-bold mb-1 ${achieved ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                                        {label}
                                                                    </div>
                                                                    {achieved ? (
                                                                        <div className="flex flex-col items-center gap-1">
                                                                            <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-bold border border-emerald-200 whitespace-nowrap">
                                                                                Completed
                                                                            </span>
                                                                            {m.awarded_at && (
                                                                                <div className="text-[9px] text-slate-400 font-medium">
                                                                                    {new Date(m.awarded_at).toLocaleDateString()}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className={`text-[9px] font-bold tracking-wider ${isNext ? 'text-blue-500' : 'text-slate-300'}`}>
                                                                            {isNext ? 'NEXT TARGET' : 'TARGET'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                        <h3 className="text-xl font-black mb-8 flex items-center gap-3 text-slate-800 tracking-tight">
                            <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600 shadow-sm"><Users size={20} /></div>
                            Network Visualization
                        </h3>
                        <div className="bg-slate-50/50 rounded-2xl overflow-hidden border border-slate-100/50">
                            <NetworkTreeVisualization data={data} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NetworkUserDetailsPage;
